import React from "react";
import { withTranslation } from "react-i18next";
import { Button, ButtonGroup, Col, Container, Row, ToggleButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import PaginationTable from "../../../../components/paginationTable/PaginationTable";
import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import ScriptsTabActionCell from "./scriptsTabActionCell/ScriptsTabActionCell";
import { textFilter } from "react-bootstrap-table2-filter";
import APIErrorHelper from "../../../../helpers/APIErrorHelper";
import DeleteTemplateScriptDialog from "./dialogs/deleteTemplateScriptDialog/DeleteTemplateScriptDialog";
import CreateTemplateScriptDialog from "./dialogs/createTemplateScriptDialog/CreateTemplateScriptDialog";
import { Link } from "react-router-dom";
import StorageService from "../../../../services/StorageService";

class ScriptsTab extends React.Component {
  tableId = "instanceScriptsTabTable";
  
  constructor(props) {
    super(props);
    let showFilters = StorageService.getTableConfigValue(this.tableId, "showFilters");

    this.state = {
      apiError: "",
      dialogApiError: "",
      unassociatedScripts: [],
      associatedScripts: [],
      templateScripts: [],
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      closeAction: () => {
      },
      showDeleteModal: false,
      showCreateModal: false,
      templateScriptToDelete: undefined
    };

    this.fetchData = this.fetchData.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  async fetchData() {
    let templateScripts = await CamplaCoreService.getAllTemplateScripts();
    templateScripts = templateScripts.filter(
      ts => ts.instanceTemplateId === this.props.instanceTemplate._id
    );

    const allScripts = await CamplaCoreService.getAllScripts();
    const unassociatedScripts = allScripts.filter(
      s => templateScripts.filter(ts => ts.scriptId === s._id).length === 0
    );

    const associatedScripts = [];
    for (const ts of templateScripts) {
      associatedScripts.push(await CamplaCoreService.getScript(ts.scriptId));
    }
    this.setState({
      templateScripts,
      associatedScripts: [...associatedScripts],
      unassociatedScripts
    });
  }

  loadCachedData() {
    const cachedTemplateScripts = StorageService.getReferenceType(
      "templateScripts"
    );
    if (cachedTemplateScripts) {
      const cachedUnassociatedTemplateScripts = StorageService.getReferenceType(
        "unassociatedTemplateScripts"
      );
      const cachedAssociatedTemplateScripts = StorageService.getReferenceType(
        "associatedTemplateScripts"
      );
      this.setState({
        templateScript: cachedTemplateScripts,
        unassociatedScripts: cachedUnassociatedTemplateScripts,
        associatedScripts: cachedAssociatedTemplateScripts
      });
    }
  }

  async componentDidMount() {
    this.loadCachedData();
    await this.fetchData();
  }

  async onRemove() {
    try {
      const templateScript = this.state.templateScripts.filter(
        ts =>
          ts.scriptId === this.state.templateScriptToDelete._id &&
          ts.instanceTemplateId === this.props.instanceTemplate._id
      )[0];
      await CamplaCoreService.deleteTemplateScript(templateScript._id);

      const updatedTemplateScripts = this.state.templateScripts.filter(
        ts => ts._id !== templateScript._id
      );
      const updatedUnassociatedScripts = [
        ...this.state.unassociatedScripts,
        this.state.templateScriptToDelete
      ];
      const updatedAssociatedScripts = this.state.associatedScripts.filter(
        as => as._id !== this.state.templateScriptToDelete._id
      );

      StorageService.set("templateScripts", updatedTemplateScripts);
      StorageService.set(
        "unassociatedTemplateScripts",
        updatedUnassociatedScripts
      );
      StorageService.set("associatedTemplateScripts", updatedAssociatedScripts);

      this.setState({
        showDeleteModal: false,
        templateScripts: updatedTemplateScripts,
        unassociatedScripts: updatedUnassociatedScripts,
        associatedScripts: updatedAssociatedScripts
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleInstanceTemplate.ScriptsTab.APIError.DeletePreconditionFailed",
            NOT_FOUND: "SingleInstanceTemplate.ScriptsTab.APIError.NotFound",
            CONFLICT:
              "SingleInstanceTemplate.ScriptsTab.APIError.DeleteConflict"
          },
          this.props.t
        )
      });
    }
  }

  async onSubmit(templateScriptToCreate) {
    try {
      templateScriptToCreate.instanceTemplateId = this.props.instanceTemplate._id;
      const createdTemplateScript = await CamplaCoreService.createTemplateScript(
        templateScriptToCreate
      );

      const updatedTemplateScripts = [
        ...this.state.templateScripts,
        createdTemplateScript
      ];
      const updatedAssociatedScripts = [
        ...this.state.associatedScripts,
        ...this.state.unassociatedScripts.filter(
          s => s._id === createdTemplateScript.scriptId
        )
      ];
      const updatedUnassociatedScripts = this.state.unassociatedScripts.filter(
        s => s._id !== createdTemplateScript.scriptId
      );

      StorageService.set("templateScripts", updatedTemplateScripts);
      StorageService.set(
        "unassociatedTemplateScripts",
        updatedUnassociatedScripts
      );
      StorageService.set("associatedTemplateScripts", updatedAssociatedScripts);

      this.setState({
        showCreateModal: false,
        templateScripts: updatedTemplateScripts,
        unassociatedScripts: updatedUnassociatedScripts,
        associatedScripts: updatedAssociatedScripts
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleInstanceTemplate.ScriptsTab.APIError.CreatePreconditionFailed",
            NOT_FOUND: "SingleInstanceTemplate.ScriptsTab.APIError.NotFound",
            CONFLICT:
              "SingleInstanceTemplate.ScriptsTab.APIError.CreateConflict"
          },
          this.props.t
        )
      });
    }
  }

  render() {
    const { t } = this.props;
    const columns = [
      {
        dataField: "name",
        text: t("SingleInstanceTemplate.ScriptsTab.ColumnName"),
        sort: true,
        filter: textFilter({
          style: { display: this.state.displayFilter },
          placeholder: t(
            "SingleInstanceTemplate.ScriptsTab.FilterNamePlaceholder"
          )
        }),
        formatter: (name, script) => (
          <Link
            className="item-link"
            to={{
              pathname: `/scripts/${script._id}`,
              state: {
                script
              }
            }}
          >
            {name}
          </Link>
        )
      },
      {
        dataField: "description",
        text: t("SingleInstanceTemplate.ScriptsTab.ColumnDescription"),
        sort: true,
        formatter: description => {
          return (
            <p>{description[this.props.i18n.language] || description["de"]}</p>
          );
        }
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
          <ButtonGroup
            toggle
            style={{ float: "right", marginRight: "-12px" }}
          >
            <ToggleButton
              type="checkbox"
              value={1}
              className="btn-green btn-sm"
              checked={this.state.showFilters}
              onChange={e => {
                this.setState({
                  showFilters: e.currentTarget.checked,
                  displayFilter: e.currentTarget.checked ? "initial" : "none"
                });
                StorageService.setTableConfigValue(this.tableId, "showFilters", e.currentTarget.checked);
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </ToggleButton>
          </ButtonGroup>
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatter: (cell, row) => {
          return (
            <ScriptsTabActionCell
              onRemove={() => {
                this.setState({
                  showDeleteModal: true,
                  templateScriptToDelete: Object.assign({}, row)
                });
              }}
            />
          );
        }
      }
    ];

    return (
      <div>
        <DeleteTemplateScriptDialog
          show={this.state.showDeleteModal}
          onHide={() => this.setState({ showDeleteModal: false })}
          onSubmit={this.onRemove}
          templateScriptName={this.state.templateScriptToDelete?.name}
          dialogApiError={this.state.dialogApiError}
        />
        <CreateTemplateScriptDialog
          show={this.state.showCreateModal}
          unassociatedScripts={this.state.unassociatedScripts || []}
          onHide={() => this.setState({ showCreateModal: false })}
          onSubmit={this.onSubmit}
        />
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row className="mt-3">
            <Col>
              <h4>{t("SingleInstanceTemplate.ScriptsTab.Title")}</h4>
            </Col>
            <Col>
              <Button
                className="btn-green"
                style={{ float: "right" }}
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <PaginationTable
                data={this.state.associatedScripts}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                ref={this.paginationTableRef}
                id={this.tableId}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withTranslation()(ScriptsTab);
